import type { ReactNode } from 'react';
import type { ConnectorStatus } from './api.js';

/**
 * Shared presentation pieces.
 *
 * The vocabulary lives here so a status can never be described one way on the
 * dashboard and another way on the connections screen.
 */

const STATUS_STYLE: Record<ConnectorStatus, { label: string; className: string }> = {
  CONNECTED: { label: 'Connected', className: 'bg-emerald-50 text-emerald-800 ring-emerald-600/20' },
  PENDING_VERIFICATION: { label: 'Pending verification', className: 'bg-amber-50 text-amber-800 ring-amber-600/20' },
  REAUTH_REQUIRED: { label: 'Reconnection required', className: 'bg-orange-50 text-orange-800 ring-orange-600/20' },
  ERROR: { label: 'Error', className: 'bg-red-50 text-red-800 ring-red-600/20' },
  DISCONNECTED: { label: 'Disconnected', className: 'bg-slate-100 text-slate-700 ring-slate-500/20' },
  HISTORICAL_RETAINED: { label: 'Stored mail retained', className: 'bg-slate-100 text-slate-700 ring-slate-500/20' },
  NOT_CONNECTED: { label: 'Not connected', className: 'bg-slate-100 text-slate-600 ring-slate-500/20' },
  NOT_CONFIGURED: { label: 'Not configured', className: 'bg-slate-100 text-slate-600 ring-slate-500/20' },
  COMING_SOON: { label: 'Unavailable', className: 'bg-slate-100 text-slate-600 ring-slate-500/20' },
};

export function StatusBadge({ status }: { status: ConnectorStatus }) {
  const s = STATUS_STYLE[status];
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${s.className}`}>
      {s.label}
    </span>
  );
}

/** What each status MEANS, in the words a person would use. */
export const STATUS_EXPLANATION: Record<ConnectorStatus, string> = {
  CONNECTED: 'Verified with a read-only call. Ready to use.',
  PENDING_VERIFICATION: 'A key is stored but has never been checked. Verify it to confirm it works.',
  REAUTH_REQUIRED: 'The key was rejected. It was probably revoked or replaced — supply a new one.',
  ERROR: 'The last check failed. Try verifying again.',
  DISCONNECTED: 'The credential was removed. The history is kept.',
  HISTORICAL_RETAINED: 'No mailbox is connected. Mail read earlier is still stored and can be deleted.',
  NOT_CONNECTED: 'No credential stored yet.',
  NOT_CONFIGURED: 'Not available in this workspace. Nothing is being read.',
  COMING_SOON: 'Not available. Nothing is connected and no data is being read.',
};

export function Card({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div className={`rounded-card border border-cp-border bg-white shadow-card ${className}`}>{children}</div>
  );
}

export function SectionHeading({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="mb-3">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">{title}</h2>
      {hint ? <p className="mt-1 text-sm text-slate-500">{hint}</p> : null}
    </div>
  );
}

export function Stat({ label, value, hint }: { label: string; value: ReactNode; hint?: string }) {
  return (
    <Card className="p-4">
      <dt className="text-sm text-slate-500">{label}</dt>
      <dd className="mt-1 text-2xl font-semibold tabular-nums text-slate-900">{value}</dd>
      {hint ? <p className="mt-1 text-xs text-slate-500">{hint}</p> : null}
    </Card>
  );
}

/** An empty state that says what happens next rather than just "nothing here". */
export function EmptyState({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="rounded-card border border-dashed border-slate-300 bg-white/60 p-6 text-center">
      <p className="text-sm font-medium text-slate-700">{title}</p>
      {children ? <div className="mx-auto mt-1 max-w-md text-sm text-slate-500">{children}</div> : null}
    </div>
  );
}

export function Spinner({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="flex items-center gap-2 text-sm text-slate-500" role="status" aria-live="polite">
      <span className="h-3 w-3 animate-spin rounded-full border-2 border-slate-300 border-t-cp-blue-600" />
      {label}
    </div>
  );
}

export function Alert({ tone, children }: { tone: 'error' | 'success' | 'info'; children: ReactNode }) {
  const cls = tone === 'error'
    ? 'border-red-200 bg-red-50 text-red-800'
    : tone === 'success'
      ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
      : 'border-sky-200 bg-sky-50 text-sky-900';
  return <div className={`rounded-xl border px-3 py-2 text-sm ${cls}`} role="status">{children}</div>;
}

export function Button({
  children, onClick, disabled, variant = 'primary', type = 'button', className = '',
}: {
  children: ReactNode; onClick?: () => void; disabled?: boolean;
  variant?: 'primary' | 'secondary'; type?: 'button' | 'submit';
  /** For per-site adjustments such as a touch-friendly minimum height. */
  className?: string;
}) {
  const base = 'inline-flex min-h-11 items-center justify-center rounded-xl px-4 py-2 text-sm font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cp-blue-600';
  const cls = variant === 'primary'
    ? 'bg-cp-blue-600 text-white shadow-sm hover:bg-cp-blue-700'
    : 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-50';
  return (
    <button type={type} onClick={onClick} disabled={disabled} className={`${base} ${cls} ${className}`}>
      {children}
    </button>
  );
}

export function formatWhen(iso: string | null): string {
  /*
   * "never" is a claim, and it was the wrong one.
   *
   * It reads as "this message has never arrived", which is nonsense for a classified
   * email — and it was printed for over a thousand Review rows that had no message
   * behind them at all. A missing timestamp means we cannot say when, not that there was
   * no when.
   */
  if (!iso) return 'Time unavailable';
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}
